import { ForwardedRef, forwardRef, useMemo, useState } from 'react';
import { ICommonFormProps } from '../DynamicForm.types';
import { isFormValidationError } from '@src/utilities/form';
import { useCopyToClipboard } from '@src/hooks';
import { IconLabel, Input } from '@src/components/ui';
import {
    EyeOutlined,
    EyeInvisibleOutlined,
    CloseCircleOutlined,
} from '@ant-design/icons';
import { InputRef } from 'antd';
import { isValidPassword } from '@src/utilities/validation';

export const FormPassword = forwardRef(
    (props: ICommonFormProps, ref: ForwardedRef<InputRef>) => {
        const { copyToClipboardAddOn } = useCopyToClipboard({});
        const [showPassword, setShowPassword] = useState(false);

        const labelText = props.label;
        const { size, ...inputProps } = props.inputProperties || {};

        const getPasswordScore = (password: string): number => {
            if (!password) return 0;

            let score = 0;
            if (/[a-z]/.test(password)) score++;
            if (/[A-Z]/.test(password)) score++;
            if (/[0-9]/.test(password)) score++;
            if (/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/.test(password)) score++;

            if (password.length >= 8) score++;

            return Math.min(score, 5);
        };

        const passwordValue =
            props.field?.value ?? props.formProps?.defaultValue ?? '';
        const score = useMemo(
            () => getPasswordScore(passwordValue),
            [passwordValue]
        );

        const strengthColors = [
            'bg-red-500',
            'bg-orange-500',
            'bg-yellow-500',
            'bg-green-400',
            'bg-green-600',
        ];
        const strengthTextColors = [
            'text-red-500',
            'text-orange-500',
            'text-yellow-600',
            'text-green-500',
            'text-green-600',
        ];

        return (
            <div className="w-full">
                <Input
                    className={`text-base ${props.className ?? ''}`}
                    ref={ref}
                    disabled={props.disabled || false}
                    label={labelText}
                    error={
                        props.errors
                            ? isFormValidationError(props.errors.type)
                                ? props.errors?.message
                                : `${props.label} ${props.errors.type}`
                            : undefined
                    }
                    data-component-name={props.keyName}
                    {...inputProps}
                    size={size}
                    placeholder={props.inputProperties?.placeholder || ''}
                    type={showPassword ? 'text' : 'password'}
                    {...props.field}
                    onChange={
                        props.formProps?.onChange
                            ? (e) => {
                                  props.formProps?.onChange(e);
                                  if (props.setValue) {
                                      props.setValue(
                                          props.keyName,
                                          e.target.value
                                      );
                                      props.triggerValidation!(props.keyName);
                                  }
                              }
                            : props.field?.onChange
                    }
                    onBlur={props.formProps?.onBlur ?? props.field?.onBlur}
                    isRequired={
                        typeof props.registerOptions?.required === 'object'
                            ? Boolean(props.registerOptions.required.value)
                            : Boolean(props.registerOptions?.required)
                    }
                    defaultValue={props.formProps?.defaultValue}
                    maxLength={props.formProps?.maxLength}
                    showCount={props.formProps?.showCount}
                    addonBefore={props.formProps?.addonBefore}
                    addonAfter={
                        props.formProps?.hasCopyAction &&
                        copyToClipboardAddOn(
                            props.field?.value ?? props.formProps?.defaultValue
                        )
                    }
                    suffix={
                        showPassword ? (
                            <EyeInvisibleOutlined
                                onClick={() => setShowPassword(false)}
                                style={{ cursor: 'pointer' }}
                            />
                        ) : (
                            <EyeOutlined
                                onClick={() => setShowPassword(true)}
                                style={{ cursor: 'pointer' }}
                            />
                        )
                    }
                    help={props.formProps?.help}
                />
                {props?.formProps?.showPasswordHelper &&
                    props.errors &&
                    !isValidPassword(passwordValue) && (
                        <div className="mt-1 flex flex-col gap-1 text-M-12 text-error bg-background p-4 rounded-2xl">
                            {!/[a-z]/.test(passwordValue) && (
                                <div className="flex items-center gap-1">
                                    <CloseCircleOutlined />
                                    At least one lowercase letter (a, b,..., z)
                                </div>
                            )}
                            {!/[A-Z]/.test(passwordValue) && (
                                <div className="flex items-center gap-1">
                                    <CloseCircleOutlined />
                                    At least one uppercase letter (A, B,..., Z)
                                </div>
                            )}
                            {!/[0-9]/.test(passwordValue) && (
                                <div className="flex items-center gap-1">
                                    <CloseCircleOutlined />
                                    At least one number (0, 1, 2,..., 9)
                                </div>
                            )}
                            {!/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/.test(
                                passwordValue
                            ) && (
                                <div className="flex items-center gap-1">
                                    <CloseCircleOutlined />
                                    At least one special character (@,%,&,...)
                                </div>
                            )}
                            {passwordValue.length < 8 && (
                                <div className="flex items-center gap-1">
                                    <CloseCircleOutlined />
                                    At least 8 characters
                                </div>
                            )}
                        </div>
                    )}
                {props?.formProps?.showStrengthMeter && passwordValue && (
                    <div className="mt-2">
                        <div className="flex gap-1">
                            {[...Array(5)].map((_, i) => (
                                <div
                                    key={i}
                                    className={`h-1 flex-1 rounded ${
                                        i < score
                                            ? strengthColors[score - 1]
                                            : 'bg-gray-200'
                                    }`}
                                />
                            ))}
                        </div>
                        {score > 0 && (
                            <div className="mt-1 text-sm font-medium">
                                Your password must contain at least 8
                                characters.
                            </div>
                        )}
                    </div>
                )}
                {props.formProps?.info && (
                    <IconLabel
                        title={props.formProps?.info}
                        type={props.formProps?.infoType}
                        rootClassName="mt-3"
                    />
                )}
            </div>
        );
    }
);

FormPassword.displayName = 'FormPassword';
